import { computed, ref, type ComputedRef, type Ref } from "vue";
import { MEDIA_DESKTOP, MEDIA_MOBILE, MEDIA_TABLET, MEDIA_TOUCH } from "../constants/breakpoints";

/** MediaQueryList в Safari < 14: подписка только через старый addListener. */
interface LegacyMediaQueryList {
  addListener(listener: (event: MediaQueryListEvent) => void): void;
}

/**
 * Текущий класс устройства — один разделяемый источник на всё приложение.
 *
 * Состояние живёт на уровне модуля, а не внутри вызова: подписок на
 * `matchMedia` ровно столько, сколько классов устройства, независимо от числа
 * компонентов. Прежний `useIsMobile` вешал свой `resize`-слушатель в каждом
 * компоненте — на экране проценки это десятки обработчиков, каждый из которых
 * дёргает реактивность при любом изменении ширины.
 *
 * `matchMedia` вместо `window.innerWidth` — потому что браузер сам сообщает о
 * пересечении границы. Слушатель `resize` срабатывает на каждый пиксель, в том
 * числе при появлении экранной клавиатуры, когда класс устройства не меняется.
 */

type Kind = "mobile" | "tablet" | "desktop" | "touch";

const QUERIES: Record<Kind, string> = {
  mobile: MEDIA_MOBILE,
  tablet: MEDIA_TABLET,
  desktop: MEDIA_DESKTOP,
  touch: MEDIA_TOUCH,
};

const state = {} as Record<Kind, Ref<boolean>>;
let initialized = false;

/** SSR и тестовая среда без `matchMedia`: считаем десктопом и не падаем. */
function hasMatchMedia(): boolean {
  return typeof window !== "undefined" && typeof window.matchMedia === "function";
}

function init(): void {
  if (initialized) return;
  initialized = true;

  (Object.keys(QUERIES) as Kind[]).forEach((kind) => {
    if (!hasMatchMedia()) {
      state[kind] = ref(kind === "desktop");
      return;
    }

    const list = window.matchMedia(QUERIES[kind]);
    const flag = ref(list.matches);
    const onChange = (event: MediaQueryListEvent) => {
      flag.value = event.matches;
    };

    // Safari < 14 не знает addEventListener на MediaQueryList — там только
    // устаревший addListener. Кабинет открывают и с рабочих iPad прошлых лет.
    if (typeof list.addEventListener === "function") {
      list.addEventListener("change", onChange);
    } else {
      const legacyList: LegacyMediaQueryList = list;
      legacyList.addListener(onChange);
    }

    state[kind] = flag;
  });
}

export interface Breakpoints {
  /** Телефон: ширина ≤ 767. */
  isMobile: Ref<boolean>;
  /** Планшет: 768–1023. */
  isTablet: Ref<boolean>;
  /** Десктоп: ≥ 1024. */
  isDesktop: Ref<boolean>;
  /** Указатель без наведения (палец, стилус) — независимо от ширины. */
  isTouch: Ref<boolean>;
  /** Телефон или планшет — там, где раскладка перестаёт быть десктопной. */
  isCompact: ComputedRef<boolean>;
}

export function useBreakpoints(): Breakpoints {
  init();

  return {
    isMobile: state.mobile,
    isTablet: state.tablet,
    isDesktop: state.desktop,
    isTouch: state.touch,
    isCompact: computed(() => state.mobile.value || state.tablet.value),
  };
}

/**
 * Сброс разделяемого состояния — только для тестов: между кейсами подменяется
 * `matchMedia`, и без сброса второй кейс читал бы подписки первого.
 */
export function resetBreakpointsForTest(): void {
  initialized = false;
  (Object.keys(QUERIES) as Kind[]).forEach((kind) => delete state[kind]);
}
